import React, { useEffect } from 'react';
import { useParseContractContext, useParseContractContextRequired } from 'features/tasks-forms';
import {
  PlanTask,
  useAiLauncherService, usePlanTasksService,
  useProjectDetailsFieldsService, ValidationError,
} from 'nekst-api';
import { useProjectContext } from '../../../view/ProjectContext';
import {
  CheckboxInput,
  Header4,
  List,
  LoadingAnimation, Right,
  SimpleBlock, Text, useListContextRequired,
  usePopupManager,
  useShowContextRequired,
} from 'ui-builder';
import { VerifyDataEntry } from './types';
import LaunchFromContractSteps, { LaunchFromContractStepId } from '../LaunchFromContractSteps';
import { NextButton } from '../../Buttons';
import { useParams } from 'react-router-dom';
import useLaunchFromContractNavigator from '../launchFromContractNavigator';
import { PDFViewer, Popup, PopupText, RowRepeater } from 'features/nekst-widgets';

import styles from './ValidateData.module.scss';
import { ParsedValueField } from './ParsedValueField';
import ValidateDetailsFieldsTour from './ValidateDetailsFieldsTour';

function useVerifyDataEntriesLoader() {
  const projectContext = useProjectContext();

  const projectType = projectContext.data?.type;

  const detailsFieldsService = useProjectDetailsFieldsService();

  const parsedContractContext = useParseContractContext();

  const [planTasks, setPlanTasks] = React.useState<PlanTask[]>();

  const [verifyDataEntries, setVerifyDataEntries] = React.useState<VerifyDataEntry[]>();

  const planTaskService = usePlanTasksService();

  useEffect(() => {
    if (projectContext.data?.planId) {
      planTaskService.getPlanTasks(projectContext.data.planId)
        .then((tasks) => {
          setPlanTasks(tasks);
        });
    }
  }, [projectContext.data?.planId]);

  const getData = async () => {
    if (!(projectType && parsedContractContext && planTasks)) {
      return [];
    } else {

      const detailsFieldsItems = (await detailsFieldsService.getFieldsApplicableForPlan(projectContext.data!.planId))
        .map((detailsField) => {
          let name = detailsField.name;
          if (detailsField.isProjectStartDate) {
            name += ' (Start Date)';
          } else if (detailsField.isProjectEndDate) {
            name += ' (End Date)';
          }

          return {
            detailsField,
            name,
            primary: detailsField.isProjectStartDate || detailsField.isProjectEndDate,
            parsedValue: parsedContractContext.parseResult.detailsFields[detailsField.id],
            relatedTask: planTasks.find((item) => item.linkedFieldId === detailsField.id),
          };
        });

      const startDateField = detailsFieldsItems.find((item) => item.detailsField.isProjectStartDate);
      const endDateField = detailsFieldsItems.find((item) => item.detailsField.isProjectEndDate);

      const result = [];

      if (startDateField) {
        result.push(startDateField);
      }

      if (endDateField) {
        result.push(endDateField);
      }

      result.push(
        ...detailsFieldsItems.filter(
          (item) => !item.detailsField.isProjectStartDate
            && !item.detailsField.isProjectEndDate
        )
      );

      return result as VerifyDataEntry[];
    }
  };

  useEffect(() => {
    if (parsedContractContext && projectType && planTasks) {
      getData()
        .then((data) => {
          setVerifyDataEntries(data);
        });
    }
  }, [projectType, parsedContractContext, planTasks]);

  return {
    verifyDataEntries,
    planTasks,
    fileLink: parsedContractContext?.parseResult.fileLink
  };
}

function DetailBoxNameField() {
  const showContext = useShowContextRequired<VerifyDataEntry>();

  const data = showContext.data!;

  return (
    <Text className={styles.boxName}>{data.name}</Text>
  );
}

function ApplyParseDataValidationPopup(props: {
  error: ValidationError
}) {
  const getFieldLabel = (fieldId: string) => {
    switch (fieldId) {
      case 'startDate':
        return 'Start Date';
      case 'endDate':
        return 'End Date';
      case 'name':
        return 'Transaction Name';
      default:
        return fieldId;
    }
  };

  return (
    <Popup
      title="Validation Error"
      content={
        <PopupText align="left">
          Please correct the following errors:
          <ul>
            {Object.entries(props.error.errors)
              .map(([fieldId, error]) => (
                <li key={fieldId}>{getFieldLabel(fieldId)}: {error.join('; ')}</li>
              ))}
          </ul>
        </PopupText>
      }
    />
  );
}

function DetailFieldsRow(props: { projectId: number }) {
  const showContext = useShowContextRequired<VerifyDataEntry>();

  const parseContractContext = useParseContractContextRequired();

  return (
    <SimpleBlock testID="detail-boxes-list"
                 className={[styles.boxWidget, parseContractContext.isFieldSelected(showContext.data!.detailsField?.id || 0) ? styles.selected : ''].join(' ')}>
      <SimpleBlock className={styles.boxWidgetInside}>
        <DetailBoxNameField />
        <ParsedValueField projectId={props.projectId} />
      </SimpleBlock>
      <CheckboxInput
        source="isVerified"
        label="Correct"
        hideLabel
        value={parseContractContext.isFieldSelected(showContext.data!.detailsField?.id || 0)}
        onChangeCallback={() => parseContractContext.toggleFieldSelection(showContext.data!.detailsField?.id || 0)}
      />
    </SimpleBlock>
  );
}

function SelectedFieldsCounter() {
  const listContext = useListContextRequired();

  const totalNumber = listContext.data.length;

  const parseContractContext = useParseContractContextRequired();

  return (
    <span
      className={styles.fieldsCounter}
      data-testid="selected-fields-counter"
      title="Use the checkboxes below to mark the fields you have already verified. This will help ensure all details are reviewed and accurate."
    >
      {parseContractContext.getNumberOfSelectedFields()} / {totalNumber}
    </span>
  );
}

function DetailFieldsList(props: { verifyDataEntries: VerifyDataEntry[], projectId: number }) {

  return (
    <>
      <List
        getDataFunc={async () => props.verifyDataEntries}
      >
        <Right>
          <SelectedFieldsCounter />
        </Right>
        <Header4>Detail Boxes</Header4>
        <RowRepeater blockClassName={styles.boxesList} groupClassName={styles.boxWidgetOuter}>
          <DetailFieldsRow
            projectId={props.projectId}
          />
        </RowRepeater>
      </List>
    </>

  );
}

export default function ValidateDefinedContractDataPage() {

  const {
    verifyDataEntries,
    fileLink
  } = useVerifyDataEntriesLoader();

  const params = useParams<{ id: string }>();

  const projectId = parseInt(params.id!, 10);

  const aiLauncherService = useAiLauncherService();

  const launchFromContractNavigator = useLaunchFromContractNavigator();

  const projectContext = useProjectContext();

  const popupManager = usePopupManager();

  if (verifyDataEntries) {
    return (
      <>
        <LaunchFromContractSteps currentStep={LaunchFromContractStepId.VERIFY_DATA} />
        <div
          style={{
            marginBottom: '4rem',
            overflow: 'hidden',
          }}
        >
          <NextButton
            onClick={async () => {
              try {
                const projectData = await aiLauncherService.applyDefinedData(projectId);
                projectContext._updateData(projectData);

                setTimeout(() => {
                  launchFromContractNavigator.openSelectGroupsPage();
                });

              } catch (e) {
                if (e instanceof ValidationError) {
                  popupManager.openPopup(<ApplyParseDataValidationPopup error={e} />);
                }
              }

            }}
            key="next-button"
          />
        </div>
        <SimpleBlock className={styles.verifyBlock}>
          <SimpleBlock className={styles.boxesBlock}>
            <DetailFieldsList verifyDataEntries={verifyDataEntries} projectId={projectId} />
          </SimpleBlock>
          <SimpleBlock className={styles.previewBlock}>
            {fileLink && <PDFViewer pdfUrl={fileLink} fileName="Test" />}
          </SimpleBlock>
          <ValidateDetailsFieldsTour />
        </SimpleBlock>

      </>
    );
  } else {
    return (<LoadingAnimation />);
  }
}
